import React, { useState, useEffect } from 'react';

const DragDropMatch = () => {
  const [gameState, setGameState] = useState('menu');
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [items, setItems] = useState([]);
  const [draggedItem, setDraggedItem] = useState(null);
  const [matched, setMatched] = useState([]);
  const [timeLeft, setTimeLeft] = useState(90);

  const generateMatchingPairs = (lvl) => {
    const pairTypes = [
      // Equations with answers
      () => {
        const pairs = [];
        for (let i = 0; i < 5; i++) {
          const a = Math.floor(Math.random() * 20) + lvl * 5;
          const b = Math.floor(Math.random() * 20) + lvl * 5;
          pairs.push({
            left: `${a} + ${b}`,
            right: a + b,
            id: i
          });
        }
        return pairs;
      },
      // Fractions with visuals
      () => {
        const fractions = [
          { left: '1/2', right: '50%', id: 0 },
          { left: '1/4', right: '25%', id: 1 },
          { left: '3/4', right: '75%', id: 2 },
          { left: '1/3', right: '33%', id: 3 },
          { left: '2/3', right: '67%', id: 4 }
        ];
        return fractions;
      },
      // Shapes with properties
      () => {
        const shapes = [
          { left: 'Triangle', right: '3 sides', id: 0 },
          { left: 'Square', right: '4 equal sides', id: 1 },
          { left: 'Pentagon', right: '5 sides', id: 2 },
          { left: 'Hexagon', right: '6 sides', id: 3 },
          { left: 'Circle', right: '0 sides', id: 4 }
        ];
        return shapes;
      }
    ];

    const generator = pairTypes[Math.floor(Math.random() * pairTypes.length)];
    return generator();
  };

  const startGame = () => {
    setGameState('playing');
    setScore(0);
    setLevel(1);
    setMatched([]);
    setTimeLeft(90);
    loadNewPairs(1);
  };

  const loadNewPairs = (lvl) => {
    const pairs = generateMatchingPairs(lvl);
    const shuffledRight = [...pairs.map(p => ({ ...p, side: 'right' }))].sort(() => Math.random() - 0.5);
    setItems({
      left: pairs.map(p => ({ ...p, side: 'left' })),
      right: shuffledRight
    });
    setMatched([]);
  };

  useEffect(() => {
    if (gameState === 'playing' && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(t => {
          if (t <= 1) {
            setGameState('gameover');
            return 0;
          }
          return t - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [gameState, timeLeft]);

  const handleDragStart = (item) => {
    setDraggedItem(item);
  };

  const handleDrop = (targetItem) => {
    if (draggedItem && draggedItem.id === targetItem.id && draggedItem.side !== targetItem.side) {
      setMatched([...matched, draggedItem.id]);
      setScore(s => s + 100 * level);
      
      if (matched.length + 1 >= items.left.length) {
        setLevel(l => l + 1);
        setTimeout(() => loadNewPairs(level + 1), 1000);
      }
    }
    setDraggedItem(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-100 to-purple-100 p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-5xl font-bold text-center mb-8 text-purple-800">
          🎯 Drag & Drop Match 🎨
        </h1>

        {gameState === 'menu' && (
          <div className="bg-white rounded-lg shadow-2xl p-12 text-center">
            <div className="text-6xl mb-6">🎯</div>
            <h2 className="text-3xl font-bold mb-4">Match the Pairs!</h2>
            <p className="text-xl mb-8 text-gray-600">
              Drag items from left to match with right<br/>
              Match equations, fractions, and shapes!
            </p>
            <button
              onClick={startGame}
              className="bg-gradient-to-r from-pink-500 to-purple-600 text-white px-12 py-4 rounded-full text-2xl font-bold hover:scale-110 transform transition"
            >
              START MATCHING
            </button>
          </div>
        )}

        {gameState === 'playing' && items.left && (
          <div>
            <div className="bg-white rounded-lg p-4 mb-4 flex justify-between items-center">
              <div className="text-xl font-bold">Score: {score}</div>
              <div className="text-xl font-bold">Level: {level}</div>
              <div className="text-xl font-bold">Matched: {matched.length}/{items.left.length}</div>
              <div className={`text-xl font-bold ${timeLeft < 20 ? 'text-red-600 animate-pulse' : 'text-green-600'}`}>
                Time: {timeLeft}s
              </div>
            </div>

            <div className="grid grid-cols-2 gap-8">
              {/* Left Column */}
              <div className="space-y-4">
                {items.left.map((item) => (
                  <div
                    key={`left-${item.id}`}
                    draggable={!matched.includes(item.id)}
                    onDragStart={() => handleDragStart(item)}
                    className={`p-6 rounded-lg text-center text-xl font-bold cursor-move transition ${
                      matched.includes(item.id)
                        ? 'bg-green-200 text-green-800 opacity-50'
                        : 'bg-blue-500 text-white hover:bg-blue-600'
                    }`}
                  >
                    {item.left}
                  </div>
                ))}
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                {items.right.map((item) => (
                  <div
                    key={`right-${item.id}`}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => handleDrop(item)}
                    className={`p-6 rounded-lg text-center text-xl font-bold transition ${
                      matched.includes(item.id)
                        ? 'bg-green-200 text-green-800'
                        : 'bg-purple-500 text-white hover:bg-purple-600'
                    }`}
                  >
                    {item.right}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {gameState === 'gameover' && (
          <div className="bg-white rounded-lg shadow-2xl p-12 text-center">
            <div className="text-6xl mb-6">🏆</div>
            <h2 className="text-4xl font-bold mb-4">Great Matching!</h2>
            <div className="text-3xl mb-4 text-purple-600 font-bold">Score: {score}</div>
            <div className="text-2xl mb-8 text-gray-600">Level: {level}</div>
            <button
              onClick={startGame}
              className="bg-gradient-to-r from-pink-500 to-purple-600 text-white px-12 py-4 rounded-full text-2xl font-bold hover:scale-110 transform transition"
            >
              PLAY AGAIN
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DragDropMatch;
